import { Bag, addProduct, createBag } from "@repo/apis/bag";
import { ExpandedBag } from "../utils/bag.type";
import { cache } from "../utils/cache";
import { buildBagExpansionsForApiInteraction } from "../utils/expansions";
import { handleApiInteractionError } from "../utils/handle-api-interaction-error";
import { handleApiInteractionSuccess } from "../utils/handle-api-interaction-success";
import { BagSdkMethod } from "../utils/method-initialiser";

type PublicAddProductMethod = {
  (params: { variantId: number; expand: true }): Promise<ExpandedBag>;
  (params: { variantId: number; expand?: false }): Promise<Bag>;
};
type AddProductMethod = BagSdkMethod<PublicAddProductMethod>;

export const addProductInitialiser: AddProductMethod = (config) =>
  (async ({ variantId, expand }) => {
    const {
      chameleonRequestInstance,
      store,
      lang,
      getAuthHeader,
      getIdTokenClaims,
      keyStoreDataversion,
      currency,
      sizeSchema,
      country,
      originCountry,
      shop,
    } = config;

    const authHeader = await getAuthHeader();
    const { customerId } = await getIdTokenClaims();
    const bagSummary = await cache.getSummary(config);
    let bagId = bagSummary?.bagId;
    let bagSessionToken = bagSummary?.sessionToken;

    const bagExpansions = buildBagExpansionsForApiInteraction({
      expand,
    });

    try {
      if (!bagId) {
        const createdBag = await createBag({
          chameleonRequestInstance,
          customerId,
          store,
          lang,
          keyStoreDataversion,
          authHeader,
          currency,
          sizeSchema,
          country,
          originCountry,
          shop,
          expand: bagExpansions,
        });

        bagId = createdBag.data.bag.id;
        bagSessionToken = createdBag.bagSessionToken;

        await handleApiInteractionSuccess(config, {
          bag: createdBag.data.bag,
          sessionToken: createdBag.bagSessionToken,
        });
      }

      const addProductResult = await addProduct({
        chameleonRequestInstance,
        variantId,
        store,
        lang,
        authHeader,
        asosBagSessionToken: bagSessionToken as string,
        bagId: bagId,
        expand: bagExpansions,
      });

      await handleApiInteractionSuccess(config, {
        bag: addProductResult.data.bag,
        sessionToken: addProductResult.bagSessionToken,
      });

      return addProductResult.data.bag;
    } catch (error: unknown) {
      return await handleApiInteractionError(config, error);
    }
  }) as PublicAddProductMethod;
